import {Map, List} from 'immutable';
import {handleActions} from 'redux-actions';

import * as ActionTypes from 'web-app/react/constants/action-types';
import * as ChangeRoleModalActions from 'web-app/react/change-role-modal/actions';

import Contact from './model';
import Image from '../common/image';
import Address from '../common/address';
import Token from '../common/token';
import * as Actions from './actions';

const DUMY_LOGIN_ID = 'loginId-pending';

const updateContactInformation = (contacts, newContactId, updateFunc) => {
    return contacts.update(newContactId, Contact.default, updateFunc);
};

const addContacts = (state, {contacts}) =>
    state.merge(
        Map(
            contacts.reduce(
                (object, contact) => ({
                    ...object,
                    [contact.relationId]: contact,
                }),
                {},
            ),
        ),
    );

export default handleActions(
    {
        [Actions.addContacts.type]: (state, {payload}) => addContacts(state, payload),
        [Actions.fetchChildContactsSuccess.type]: (state, {payload}) => {
            return addContacts(state, {contacts: payload.relations});
        },
        [Actions.updateContactsForChildren.type]: (state, {payload}) => {
            // Here, the type of payload.relationsMap is:
            // Map<ChildId, Map<ContactId, Contact>>
            return payload.relationMaps
                .valueSeq()
                .toArray()
                .reduce((_state, updatedContact) => {
                    return addContacts(_state, {contacts: updatedContact});
                }, state);
        },

        [Actions.updateChildContact.type]: (state, {payload}) =>
            updateContactInformation(state, payload.data.relationId, oldRelation =>
                oldRelation.merge({updating: true}),
            ),

        [Actions.updateChildContactSuccess.type]: (state, {payload}) =>
            updateContactInformation(state, payload.relationId, () => payload),

        [Actions.updateChildContactFailed.type]: (state, {payload}) =>
            updateContactInformation(state, payload.relationId, oldRelation => oldRelation.merge({updating: false})),

        [Actions.fetchChildContactSuccess.type]: (state, {payload}) =>
            updateContactInformation(state, payload.relationId, () => Contact.fromAPI(payload)),

        [Actions.fetchKikuRelationsSuccess.type]: (state, {payload}) =>
            payload.kikuRelations.reduce(
                (reduction, {relationId, kikuRelation}) =>
                    updateContactInformation(reduction, relationId, rel => rel.merge({kikuRelation})),
                state,
            ),
        [Actions.updateKikuRelationSuccess.type]: (state, {payload}) =>
            updateContactInformation(state, payload.relationId, relation =>
                relation.merge({
                    kikuRelation: payload.kikuRelation,
                }),
            ),
        [Actions.deleteChildContact.type]: (state, {payload: {contact}}) =>
            updateContactInformation(state, contact.relationId, c => c.merge({deleting: true})),
        [Actions.deleteChildContactFailed.type]: (state, {payload: {relationId}}) =>
            updateContactInformation(state, relationId, c => c.merge({deleting: false})),
        [Actions.deleteChildContactAborted.type]: (state, {payload: {relationId}}) =>
            updateContactInformation(state, relationId, c => c.merge({deleting: false})),

        [Actions.deleteChildContactSuccess.type]: (state, {payload}) => state.delete(payload.relationId),

        [Actions.fetchChildContactSsn.type]: (state, {payload}) =>
            updateContactInformation(state, payload.relationId, oldRelation => oldRelation.merge({ssnLoading: true})),

        [Actions.fetchChildContactSsnSuccess.type]: (state, {payload: {relationId, ssn, updateToken}}) =>
            updateContactInformation(state, relationId, oldRelation => {
                return oldRelation.merge({
                    ssn,
                    ssnToken: Token.fromAPI(updateToken),
                    ssnLoading: false,
                    ssnLoaded: true,
                });
            }),

        [Actions.fetchChildContactSsnFailed.type]: (state, {payload}) =>
            updateContactInformation(state, payload.relationId, oldRelation => oldRelation.merge({ssnLoading: false})),

        [ChangeRoleModalActions.removeLogin.type]: (state, {payload}) =>
            updateContactInformation(state, payload.assignee.assigneeId, oldRelation =>
                oldRelation.merge({removingLogin: true}),
            ),

        [ChangeRoleModalActions.removeLoginFailed.type]: (state, {payload}) =>
            updateContactInformation(state, payload.assignee.assigneeId, oldRelation =>
                oldRelation.merge({removingLogin: false}),
            ),

        [ChangeRoleModalActions.removeLoginAborted.type]: (state, {payload}) =>
            updateContactInformation(state, payload.assignee.assigneeId, oldRelation =>
                oldRelation.merge({removingLogin: false}),
            ),

        [Actions.fetchChildContactProtectedAddress.type]: (state, {payload}) =>
            updateContactInformation(state, payload, oldRelation => oldRelation.merge({addressLoading: true})),

        [Actions.fetchChildContactProtectedAddressSuccess.type]: (state, {payload: {relationId, address}}) =>
            updateContactInformation(state, relationId, oldRelation =>
                oldRelation.merge({addressLoading: false, addressLoaded: true, address: Address.fromAPI(address)}),
            ),

        [Actions.fetchChildContactProtectedAddressFailed.type]: (state, {payload}) =>
            updateContactInformation(state, payload.relationId, oldRelation =>
                oldRelation.merge({addressLoading: false}),
            ),

        [ActionTypes.FILE_UPLOAD_FILE_PERSIST_SUCCESS]: (state, {payload: {persistedFile, config}}) => {
            if (persistedFile.large) {
                if (config.type === 'relation') {
                    return updateContactInformation(state, config.relationId, oldRelation =>
                        oldRelation.merge({image: Image.fromAPI(persistedFile)}),
                    );
                } else if (config.type === 'employeeRelations') {
                    return updateContactInformation(state, config.employeeRelationsId, oldRelation =>
                        oldRelation.merge({image: Image.fromAPI(persistedFile)}),
                    );
                }
            }
            return state;
        },

        [Actions.createEmployeeContactSuccess.type]: (state, {payload: {contact}}) =>
            state.merge({[contact.relationId]: contact}),

        [Actions.createChildContactSuccess.type]: (state, {payload: {contact}}) =>
            state.merge({[contact.relationId]: contact}),

        [Actions.fetchEmployeeContactsSuccess.type]: (state, {payload}) =>
            addContacts(state, {contacts: payload.contacts}),

        [Actions.updateEmployeeContact.type]: (state, {payload: {data}}) =>
            updateContactInformation(state, data.relationId, c => c.merge({updating: true})),
        [Actions.updateEmployeeContactSuccess.type]: (state, {payload}) =>
            updateContactInformation(state, payload.relationId, () => payload),
        [Actions.updateEmployeeContactFailed.type]: (state, {payload}) =>
            updateContactInformation(state, payload.data, c => c.merge({updating: false})),

        [Actions.deleteEmployeeContact.type]: (state, {payload: {contact}}) =>
            updateContactInformation(state, contact.relationId, c => c.merge({deleting: true})),
        [Actions.deleteEmployeeContactSuccess.type]: (state, {payload}) => state.delete(payload.relationId),
        [Actions.deleteEmployeeContactFailed.type]: (state, {payload}) =>
            updateContactInformation(state, payload.data, c => c.merge({deleting: false})),

        [Actions.updateChildContactAfterRoleUpdate.type]: (state, {payload: {data}}) =>
            updateContactInformation(state, data.relationId, oldRelation =>
                oldRelation.merge({
                    roles2: data.role,
                    email: data.email ? data.email : oldRelation.email,
                    // Copy of the logic in the backend, contact email is editable if the contact has no login
                    // we never use the loginId value for contacts
                    loginId: data.role.size !== 0 ? DUMY_LOGIN_ID : undefined,
                    invitations: data.invitations?.size > 0 ? List(data.invitations) : List(),
                }),
            ),

        [Actions.updateContactRoles.type]: (state, {payload}) =>
            updateContactInformation(state, payload.relationId, oldRelation =>
                oldRelation.merge({roles2: payload.roles2}),
            ),
    },
    Map(),
);
